import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { api, extractApiError } from "../lib/api";
import { cn } from "../lib/cn";
import { formatFieldValue, getFieldLabel, type Entity } from "../lib/types";
import { FieldActions } from "./FieldActions";
import { FieldEditHistory } from "./FieldEditHistory";
import { FieldHistoryPopover } from "./FieldHistoryPopover";
import { useFieldHasHistory } from "../lib/EntityHistoryFieldsContext";
import type { Role } from "../lib/permissions";

/**
 * Fields whose change should also bust the renewal-status matrix cache.
 *
 * Intentionally empty: all renewal date/cycle fields (licenseRenewal,
 * companyRenewal, eorExpiryDate, annualFilingDate, and their *Cycle
 * counterparts) are owned by ObligationEditor and TypedFieldsEditor, which
 * handle their own invalidation.  InlineEdit only covers free-text fields
 * (addresses, names, notes, etc.) that do not affect obligation status.
 *
 * If a field is added here it will trigger an extra
 * ["entity-renewal-status", entityId] invalidation on every InlineEdit save
 * for that field — only add fields that genuinely affect renewal computation.
 */
const RENEWAL_FIELDS: ReadonlySet<string> = new Set<string>();

interface InlineEditProps {
  entityId: string;
  field: keyof Entity;
  value: string | null;
  canEdit: boolean;
  multiline?: boolean;
  onSuccess?: (newValue: string) => void;
  /**
   * When provided (non-null), the FieldEditHistory panel is shown in edit mode
   * so the user can see recent changes while editing.
   * (canSeeAuditLog is now true for every authenticated role, so any non-null
   * role enables the history panel.)
   */
  role?: Role | null;
}

export function InlineEdit({
  entityId,
  field,
  value,
  canEdit,
  multiline = false,
  onSuccess,
  role,
}: InlineEditProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(value ?? "");
  const qc = useQueryClient();
  const { t } = useTranslation("common");

  const display = value && value.trim().length > 0 ? formatFieldValue(field as string, value) : null;

  // Show inline history panel in edit mode when user is authenticated (role != null).
  const showHistory = role != null;

  const { t: tAudit } = useTranslation("audit");
  const fieldLabel = getFieldLabel(field as string);
  const hasHistory = useFieldHasHistory(field as string);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await api.patch(`/entities/${entityId}`, {
        field: field as string,
        value: draft,
        comment: null,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success(t("field_updated", { field: fieldLabel }));
      setEditing(false);
      onSuccess?.(draft);
      void qc.invalidateQueries({ queryKey: ["entity", entityId] });
      void qc.invalidateQueries({ queryKey: ["entities"] });
      void qc.invalidateQueries({ queryKey: ["changelog-recent"] });
      void qc.invalidateQueries({ queryKey: ["renewals"] });
      // Bust the per-entity changelog so the Change history section refreshes.
      void qc.invalidateQueries({ queryKey: ["changelog", { entityId }] });
      if (RENEWAL_FIELDS.has(field as string)) {
        void qc.invalidateQueries({ queryKey: ["entity-renewal-status", entityId] });
      }
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  if (!canEdit) {
    return (
      <div className="group flex items-center gap-2">
        <span className={cn("text-[14px] font-normal text-text", multiline && "whitespace-pre-line")}>
          {display ?? <span className="text-dim font-normal">—</span>}
        </span>
        <FieldActions
          fieldLabel={fieldLabel}
          canEdit={false}
          showClock={hasHistory}
          clockTrigger={hasHistory ? (
            <FieldHistoryPopover
              entityId={entityId}
              field={field as string}
              triggerAriaLabel={tAudit("open_field_history", { field: fieldLabel })}
            />
          ) : undefined}
        />
      </div>
    );
  }

  if (!editing) {
    return (
      <div className="group flex items-center gap-2 min-h-[34px]">
        <span className={cn("text-[14px] font-normal text-text", multiline && "whitespace-pre-line")}>
          {display ?? <span className="text-dim font-normal">—</span>}
        </span>
        <FieldActions
          fieldLabel={fieldLabel}
          canEdit={canEdit}
          showClock={hasHistory}
          clockTrigger={hasHistory ? (
            <FieldHistoryPopover
              entityId={entityId}
              field={field as string}
              triggerAriaLabel={tAudit("open_field_history", { field: fieldLabel })}
            />
          ) : undefined}
          onEdit={() => {
            setDraft(value ?? "");
            setEditing(true);
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 py-1">
      {multiline ? (
        <textarea
          // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional: inline edit focuses the field the user activated
          autoFocus
          rows={6}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-label={`${String(field)} value`}
          className={cn(
            "min-h-[120px] w-full px-3 py-2 rounded-lg border border-border bg-surface-2",
            "text-base focus:bg-surface focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/14",
            "resize-y",
          )}
        />
      ) : (
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional: inline edit focuses the field the user activated
          autoFocus
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-label={`${String(field)} value`}
          onKeyDown={(e) => {
            if (e.key === "Escape") { setEditing(false); setDraft(value ?? ""); }
            if (e.key === "Enter" && !multiline) mutation.mutate();
          }}
          className={cn(
            // Proto .row input.edit
            "text-[14px] font-semibold border border-accent rounded-[6px] px-2 py-1 w-[60%]",
            "ring-[3px] ring-accent/14 outline-none bg-surface",
          )}
        />
      )}
      {showHistory && (
        <FieldEditHistory entityId={entityId} field={field as string} />
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
          className={cn(
            "inline-flex h-9 items-center gap-1 rounded-lg bg-accent px-3",
            "text-[12.5px] font-semibold text-white hover:bg-[#4338ca]",
            "disabled:opacity-50 min-h-[44px] sm:min-h-[34px]",
            "focus:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/20",
          )}
        >
          <Check className="h-3.5 w-3.5" aria-hidden /> {t("common:save")}
        </button>
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            setDraft(value ?? "");
          }}
          className={cn(
            "inline-flex h-9 items-center gap-1 rounded-lg border border-border bg-surface px-3",
            "text-[12.5px] font-semibold text-text hover:bg-surface-2",
            "min-h-[44px] sm:min-h-[34px]",
            "focus:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/20",
          )}
        >
          <X className="h-3.5 w-3.5" aria-hidden /> {t("common:cancel")}
        </button>
      </div>
    </div>
  );
}
